// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import {
  Box,
  Breadcrumbs,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useLocation } from '@tanstack/react-router';
import { ReactNode, useEffect, useRef, useState } from 'react';

// A single navigable destination within a section's quick-jump dropdown.
interface BreadcrumbNavNode {
  label: string;
  to: string;
}

// A labeled group of destinations shown under a heading in the dropdown.
// Lets a section's menu group pages by intent (e.g. operational surfaces
// vs. analytical reports) instead of one flat list.
export interface BreadcrumbNavSection {
  heading: string;
  items: BreadcrumbNavNode[];
}

// One crumb in the trail. When `sections` is present (and the crumb is not
// the final one) the crumb gains a hover dropdown that quick-jumps to
// sibling pages within the same app section.
export interface SectionBreadcrumbItem {
  label: string;
  to?: string;
  sections?: BreadcrumbNavSection[];
}

interface Props {
  items: SectionBreadcrumbItem[];
}

const HOVER_CLOSE_DELAY_MS = 100;

// Locks every crumb to the same body2 box model so link and text items
// line up inside the MuiBreadcrumbs flex row.
const BREADCRUMB_LABEL_SX: SxProps<Theme> = {
  display: 'inline',
  lineHeight: (theme) => theme.typography.body2.lineHeight,
  fontSize: (theme) => theme.typography.body2.fontSize,
};

// A breadcrumb trail where upstream crumbs can expose a hover dropdown of
// sibling pages, turning the trail into a quick-jump nav within a section.
export function SectionBreadcrumbs(props: Props) {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <Breadcrumbs separator="›">
      {props.items.map((item, index) => {
        const isLast = index === props.items.length - 1;
        const sections = !isLast ? item.sections : undefined;

        if (sections && sections.length > 0) {
          return (
            <BreadcrumbWithDropdown
              key={`${item.label}-${index}`}
              item={item}
              sections={sections}
              currentPath={currentPath}
            />
          );
        }

        return <BreadcrumbAnchor key={`${item.label}-${index}`} item={item} />;
      })}
    </Breadcrumbs>
  );
}

interface AnchorProps {
  item: SectionBreadcrumbItem;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
}

// Renders a single breadcrumb entry either as a link (when the item has a
// target route) or as plain text (current page / non-navigable group).
function BreadcrumbAnchor(props: AnchorProps) {
  if (props.item.to) {
    return (
      <Typography
        component={Link}
        to={props.item.to}
        variant="body2"
        color="text.secondary"
        sx={BREADCRUMB_LABEL_SX}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
      >
        {props.item.label}
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      sx={BREADCRUMB_LABEL_SX}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      {props.item.label}
    </Typography>
  );
}

interface DropdownProps {
  item: SectionBreadcrumbItem;
  sections: BreadcrumbNavSection[];
  currentPath: string;
}

// Wraps a breadcrumb anchor with a hover-triggered dropdown exposing the
// sibling pages nested beneath it in the section. Items are grouped under
// section headings so users can scan the menu by intent instead of one
// flat list.
function BreadcrumbWithDropdown(props: DropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<number>();

  useEffect(() => {
    return () => {
      window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Opens the menu and cancels any pending close from leaving the link.
  const openMenu = (element: HTMLElement) => {
    window.clearTimeout(closeTimerRef.current);
    setAnchorEl(element);
  };

  // Defers closing so the pointer can travel from the link to the menu.
  const scheduleClose = () => {
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleSelect = () => {
    window.clearTimeout(closeTimerRef.current);
    setAnchorEl(null);
  };

  return (
    <Box component="span" sx={{ display: 'contents' }}>
      <BreadcrumbAnchor
        item={props.item}
        onMouseEnter={(event) => openMenu(event.currentTarget)}
        onMouseLeave={scheduleClose}
      />
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper
          elevation={4}
          sx={{ minWidth: '12rem' }}
          onMouseEnter={() => window.clearTimeout(closeTimerRef.current)}
          onMouseLeave={scheduleClose}
        >
          {/* MenuList only accepts a flat children array (no
              fragments — MUI logs a warning otherwise). flatMap
              produces one array of dividers + headings + items. */}
          <MenuList dense>
            {props.sections.flatMap<ReactNode>((section, sectionIdx) => {
              const nodes: ReactNode[] = [];
              if (sectionIdx > 0) {
                nodes.push(
                  <Divider
                    key={`${section.heading}-divider`}
                    sx={{ my: 0.5 }}
                  />
                );
              }
              nodes.push(
                <Typography
                  key={`${section.heading}-heading`}
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: 0.5,
                    pb: 0.25,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {section.heading}
                </Typography>
              );
              for (const child of section.items) {
                const isCurrent = child.to === props.currentPath;
                if (isCurrent) {
                  nodes.push(
                    <MenuItem
                      key={child.to}
                      selected
                      disableRipple
                      sx={{ cursor: 'default', pointerEvents: 'none' }}
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight={600}
                      >
                        {child.label}
                      </Typography>
                    </MenuItem>
                  );
                } else {
                  nodes.push(
                    <MenuItem
                      key={child.to}
                      component={Link}
                      to={child.to}
                      onClick={handleSelect}
                    >
                      <Typography variant="body2">{child.label}</Typography>
                    </MenuItem>
                  );
                }
              }
              return nodes;
            })}
          </MenuList>
        </Paper>
      </Popper>
    </Box>
  );
}
